import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import { api } from '../../lib/api';
import { TransaksiPembayaran, JenisPembayaran, Kelas, TahunAjaran, KwitansiData } from '../../types';
import { TableSkeleton } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { KwitansiModal } from '../../components/keuangan/KwitansiModal';

import {
  FileSpreadsheet,
  Filter,
  Search,
  Calendar,
  Wallet,
  Printer,
  CheckCircle2,
  Ban,
  Download,
  DollarSign,
  TrendingUp,
} from 'lucide-react';



const formatTanggalPanjang = (iso: string) =>
  new Date(iso + 'T00:00:00').toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

export function LaporanKeuanganPage() {
  // Date range defaults: first day of this month to today
  const today = new Date().toISOString().slice(0, 10);
  const firstDayOfMonth = today.slice(0, 7) + '-01';

  const [startDate, setStartDate] = useState(firstDayOfMonth);
  const [endDate, setEndDate] = useState(today);
  const [selectedStatus, setSelectedStatus] = useState<string>('valid');
  const [selectedMetode, setSelectedMetode] = useState<string>('semua');
  const [selectedJenisId, setSelectedJenisId] = useState<string>('');
  const [selectedKelasId, setSelectedKelasId] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [selectedTxId, setSelectedTxId] = useState<number | null>(null);

  // Ukuran kertas & orientasi diatur langsung di dialog cetak browser
  const handlePrint = () => window.print();

  // Queries
  const { data: taList = [] } = useQuery({
    queryKey: ['tahun-ajaran'],
    queryFn: async () => {
      const res = await api.get<TahunAjaran[]>('/api/tahun-ajaran');
      return res.data || [];
    },
  });

  const activeTa = taList.find((t) => t.is_active) || taList[0];



  const { data: kelasList = [] } = useQuery({
    queryKey: ['kelas-simple'],
    queryFn: async () => {
      const res = await api.get<Kelas[]>('/api/kelas/simple');
      return res.data || [];
    },
  });

  const { data: jenisList = [] } = useQuery({
    queryKey: ['jenis-pembayaran'],
    queryFn: async () => {
      const res = await api.get<JenisPembayaran[]>('/api/keuangan/jenis');
      return res.data || [];
    },
  });

  // Query Laporan Transaksi
  const { data: laporanResponse, isLoading } = useQuery({
    queryKey: [
      'laporan-keuangan',
      startDate,
      endDate,
      selectedStatus,
      selectedMetode,
      selectedJenisId,
      selectedKelasId,
      search,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (selectedStatus && selectedStatus !== 'semua') params.append('status', selectedStatus);
      if (selectedMetode && selectedMetode !== 'semua') params.append('metode', selectedMetode);
      if (selectedJenisId) params.append('jenis_id', selectedJenisId);
      if (selectedKelasId) params.append('kelas_id', selectedKelasId);
      if (search) params.append('search', search);
      params.append('limit', '500'); // Muat hingga 500 baris untuk laporan

      const res = await api.get<any>(`/api/keuangan/pembayaran?${params.toString()}`);
      return res;
    },
  });

  const transaksiList: TransaksiPembayaran[] = laporanResponse?.data || (laporanResponse as any)?.items || [];
  const summary = laporanResponse?.summary || { totalNominalValid: 0, totalNominalBatal: 0 };

  // Profil madrasah untuk kop surat: pakai sumber yang sama dengan kwitansi
  const sampleTxId = transaksiList[0]?.id;
  const { data: profil } = useQuery({
    queryKey: ['profil-madrasah-kop', sampleTxId],
    queryFn: async () => {
      const res = await api.get<KwitansiData>(`/api/keuangan/pembayaran/${sampleTxId}/kwitansi`);
      return res.data?.madrasah || null;
    },
    enabled: Boolean(sampleTxId),
    staleTime: 5 * 60 * 1000,
  });

  const kopBaris2 = profil?.alamat || '';
  const kopBaris3 = [
    profil?.nsm ? `NSM: ${profil.nsm}` : '',
    profil?.npsn ? `NPSN: ${profil.npsn}` : '',
    profil?.telepon ? `Telp: ${profil.telepon}` : '',
  ].filter(Boolean).join(' • ');

  const kotaProfil = String((profil as any)?.kabupaten_kota || '').replace(
    /^(Kota|Kabupaten|Kab\.)\s+/i,
    ''
  );

  // Rekap per Pos Pembayaran (mengikuti filter yang sedang aktif)
  const rekapPerPos = useMemo(() => {
    const map = new Map<string, { pos: string; jumlahTransaksi: number; total: number }>();
    // Mulai dari semua pos (nilai 0), atau hanya pos yang dipilih di filter
    jenisList
      .filter((j) => !selectedJenisId || String(j.id) === selectedJenisId)
      .forEach((j) => map.set(j.nama, { pos: j.nama, jumlahTransaksi: 0, total: 0 }));
    for (const tx of transaksiList) {
      const pos = tx.tagihan?.jenisPembayaran?.nama || 'Pos Pembayaran';
      const row = map.get(pos) ?? { pos, jumlahTransaksi: 0, total: 0 };
      row.jumlahTransaksi += 1;
      row.total += Number(tx.jumlah_bayar) || 0;
      map.set(pos, row);
    }
    return Array.from(map.values());
  }, [transaksiList, jenisList, selectedJenisId]);

  const rekapTotalTransaksi = rekapPerPos.reduce((s, r) => s + r.jumlahTransaksi, 0);
  const rekapTotalNominal = rekapPerPos.reduce((s, r) => s + r.total, 0);

  // Export Excel menggunakan SheetJS (xlsx)
  const handleExportExcel = () => {
    if (transaksiList.length === 0) return;

    const exportData = transaksiList.map((tx, idx) => ({
      No: idx + 1,
      'No. Kwitansi': tx.nomor_transaksi,
      Tanggal: tx.tanggal_bayar,
      'Nama Siswa': tx.siswa?.nama || '-',
      NIS: tx.siswa?.nis || '-',
      Kelas: tx.tagihan?.kelas_id ? `Kelas ${tx.siswa?.kelas_id}` : '-',
      'Pos Pembayaran': tx.tagihan?.jenisPembayaran?.nama || 'Tagihan',
      Bulan: tx.tagihan?.bulan || '-',
      'Nominal (Rp)': Number(tx.jumlah_bayar),
      Metode: tx.metode,
      Petugas: tx.createdByUser?.nama_lengkap || 'Kasir',
      Status: tx.status === 'valid' ? 'Sah / Valid' : `Dibatalkan (${tx.alasan_batal || ''})`,
      Catatan: tx.catatan || '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Laporan Pembayaran');

    const rekapSheet = XLSX.utils.json_to_sheet([
      ...rekapPerPos.map((r, i) => ({
        No: i + 1,
        'Pos Pembayaran': r.pos,
        'Jml Transaksi': r.jumlahTransaksi,
        'Total (Rp)': r.total,
      })),
      { No: '', 'Pos Pembayaran': 'TOTAL', 'Jml Transaksi': rekapTotalTransaksi, 'Total (Rp)': rekapTotalNominal },
    ]);
    XLSX.utils.book_append_sheet(workbook, rekapSheet, 'Rekap per Pos');

    const fileName = `Laporan_Keuangan_Madrasah_${startDate}_sd_${endDate}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  return (
    <div className="space-y-6">
      <style>{`
        @media print {
          @page { size: auto; margin: 8mm; }
          html, body, #root {
            height: auto !important;
            overflow: visible !important;
            background: #fff !important;
          }
          body * { visibility: hidden !important; }
          .laporan-sheet, .laporan-sheet * { visibility: visible !important; }
          .laporan-sheet {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            max-width: none !important;
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            overflow: visible !important;
          }
          .laporan-sheet thead { display: table-header-group; }
          .laporan-sheet tr { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800">
            Laporan Keuangan & Kasir
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Rekapitulasi transaksi pembayaran harian & bulanan madrasah serta ekspor dokumen Excel (.xlsx).
          </p>
        </div>

        <div className="flex items-center gap-2 print:hidden">
          <button
            onClick={() => handleExportExcel()}
            disabled={transaksiList.length === 0}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-emerald-700 hover:bg-emerald-800 disabled:bg-slate-300 text-white rounded-xl text-xs font-semibold shadow-xs transition-colors cursor-pointer"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Ekspor ke Excel (.xlsx)
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 print:hidden">
        <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs">
          <span className="text-[10px] uppercase font-bold text-slate-500 block">
            Total Kas Diterima (Valid)
          </span>
          <h3 className="text-2xl font-extrabold font-mono text-emerald-700 mt-1">
            Rp {Number(summary.totalNominalValid).toLocaleString('id-ID')}
          </h3>
          <p className="text-[11px] text-slate-400 mt-0.5">
            Dari {transaksiList.filter((t) => t.status === 'valid').length} transaksi berhasil
          </p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs">
          <span className="text-[10px] uppercase font-bold text-slate-500 block">
            Total Transaksi Dibatalkan (Void)
          </span>
          <h3 className="text-2xl font-extrabold font-mono text-rose-600 mt-1">
            Rp {Number(summary.totalNominalBatal).toLocaleString('id-ID')}
          </h3>
          <p className="text-[11px] text-slate-400 mt-0.5">
            {transaksiList.filter((t) => t.status === 'dibatalkan').length} transaksi di-void
          </p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs">
          <span className="text-[10px] uppercase font-bold text-slate-500 block">
            Periode Laporan
          </span>
          <h3 className="text-sm font-bold text-slate-800 mt-1.5 flex items-center gap-1.5">
            <Calendar className="w-4 h-4 text-emerald-600" />
            {startDate} s.d. {endDate}
          </h3>
          <p className="text-[11px] text-slate-400 mt-0.5">
            Tahun Ajaran {activeTa?.tahun || '-'}
          </p>
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs space-y-3 print:hidden">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {/* Dari Tanggal */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">
              Dari Tanggal
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-1.5 text-xs border border-slate-200 rounded-xl focus:outline-emerald-600"
            />
          </div>

          {/* Sampai Tanggal */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">
              Sampai Tanggal
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-1.5 text-xs border border-slate-200 rounded-xl focus:outline-emerald-600"
            />
          </div>

          {/* Status Transaksi */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">
              Status Transaksi
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-1.5 text-xs border border-slate-200 rounded-xl focus:outline-emerald-600 bg-white"
            >
              <option value="semua">Semua Transaksi</option>
              <option value="valid">Sah / Valid Saja</option>
              <option value="dibatalkan">Dibatalkan (Void) Saja</option>
            </select>
          </div>

          {/* Metode Bayar */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">
              Metode Pembayaran
            </label>
            <select
              value={selectedMetode}
              onChange={(e) => setSelectedMetode(e.target.value)}
              className="w-full px-3 py-1.5 text-xs border border-slate-200 rounded-xl focus:outline-emerald-600 bg-white"
            >
              <option value="semua">Semua Metode</option>
              <option value="Tunai">Tunai / Cash</option>
              <option value="Transfer">Transfer Bank</option>
            </select>
          </div>

          {/* Pos Pembayaran */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">
              Pos Pembayaran
            </label>
            <select
              value={selectedJenisId}
              onChange={(e) => setSelectedJenisId(e.target.value)}
              className="w-full px-3 py-1.5 text-xs border border-slate-200 rounded-xl focus:outline-emerald-600 bg-white"
            >
              <option value="">Semua Pos</option>
              {jenisList.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.nama}
                </option>
              ))}
            </select>
          </div>

          {/* Cari Kata Kunci */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">
              Cari Siswa / Kwitansi
            </label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-2.5" />
              <input
                type="text"
                placeholder="Nama / NIS..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-xl focus:outline-emerald-600"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tombol Cetak */}
      <div className="flex justify-end print:hidden">
        <button
          type="button"
          onClick={handlePrint}
          disabled={transaksiList.length === 0}
          className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-colors cursor-pointer"
        >
          <Printer className="w-4 h-4" />
          Cetak Laporan Keuangan
        </button>
      </div>

      {/* Tabel Data Laporan & Printable Container */}
      <div
        className="laporan-sheet bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden p-4 sm:p-6 text-slate-900"
      >
        {/* Printable Sheet Header (Muncul Saat Cetak) */}
        <div className="kop-surat print:block hidden">
          <div className="border-b border-slate-700 pb-1.5 mb-4 text-left pl-24">
            <h1 className="text-2xl font-extrabold tracking-wide text-slate-900 leading-tight uppercase">
              {profil?.nama || 'NAMA MADRASAH'}
            </h1>
            {kopBaris2 && <p className="text-[9px] font-bold text-slate-900 leading-snug">{kopBaris2}</p>}
            {kopBaris3 && <p className="text-[9px] font-bold text-slate-900 leading-snug">{kopBaris3}</p>}
          </div>
          <h2 className="text-center text-lg font-extrabold text-slate-900 mb-4">
            REKAPITULASI PENERIMAAN
          </h2>
        </div>

        {/* Print Summary Strip (Muncul Saat Cetak) */}
        <div className="hidden grid-cols-3 gap-3 p-2.5 bg-slate-50 border border-slate-300 rounded-lg mb-3 text-xs">
          <div>
            <span className="text-slate-500 block text-[9px] uppercase font-bold">Total Kas Diterima (Valid):</span>
            <span className="font-mono font-bold text-emerald-800">Rp {Number(summary.totalNominalValid).toLocaleString('id-ID')}</span>
          </div>
          <div>
            <span className="text-slate-500 block text-[9px] uppercase font-bold">Total Transaksi Void:</span>
            <span className="font-mono font-bold text-rose-800">Rp {Number(summary.totalNominalBatal).toLocaleString('id-ID')}</span>
          </div>
          <div>
            <span className="text-slate-500 block text-[9px] uppercase font-bold">Jumlah Transaksi:</span>
            <span className="font-bold text-slate-800">{transaksiList.length} Transaksi Terdata</span>
          </div>
        </div>

        {isLoading ? (
          <div className="p-6">
            <TableSkeleton rows={8} cols={8} />
          </div>
        ) : transaksiList.length === 0 ? (
          <EmptyState
            title="Tidak Ada Data Transaksi"
            description="Tidak ditemukan riwayat pembayaran pada rentang tanggal dan kriteria filter ini."
          />
        ) : (
          <div className="overflow-x-auto print:hidden">
            <table className="w-full text-xs text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500 font-semibold bg-slate-50/70">
                  <th className="py-2.5 px-2.5">No. Kwitansi</th>
                  <th className="py-2.5 px-2.5">Tanggal</th>
                  <th className="py-2.5 px-2.5">Nama Siswa</th>
                  <th className="py-2.5 px-2.5">Pos Pembayaran</th>
                  <th className="py-2.5 px-2.5">Bulan</th>
                  <th className="py-2.5 px-2.5 font-mono">Jumlah Bayar</th>
                  <th className="py-2.5 px-2.5 text-center">Metode</th>
                  <th className="py-2.5 px-2.5">Kasir / Penerima</th>
                  <th className="py-2.5 px-2.5 text-center">Status</th>
                  <th className="py-2.5 px-2.5 text-right print:hidden">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {transaksiList.map((tx) => (
                  <tr key={tx.id} className="hover:bg-slate-50/70 transition-colors">
                    <td className="py-2 px-2.5 font-mono font-bold text-slate-800">
                      {tx.nomor_transaksi}
                    </td>

                    <td className="py-2 px-2.5 text-slate-600 whitespace-nowrap">
                      {tx.tanggal_bayar}
                    </td>

                    <td className="py-2 px-2.5">
                      <div>
                        <p className="font-bold text-slate-900">{tx.siswa?.nama}</p>
                        <span className="text-[10px] text-slate-400 font-mono">
                          NIS: {tx.siswa?.nis}
                        </span>
                      </div>
                    </td>

                    <td className="py-2 px-2.5 font-medium text-slate-800">
                      {tx.tagihan?.jenisPembayaran?.nama || 'Pos Pembayaran'}
                    </td>

                    <td className="py-2 px-2.5 text-slate-600">
                      {tx.tagihan?.bulan || '-'}
                    </td>

                    <td className="py-2 px-2.5 font-mono font-bold text-emerald-700">
                      Rp {Number(tx.jumlah_bayar).toLocaleString('id-ID')}
                    </td>

                    <td className="py-2 px-2.5 text-center">
                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-700">
                        {tx.metode}
                      </span>
                    </td>

                    <td className="py-2 px-2.5 text-slate-600">
                      {tx.createdByUser?.nama_lengkap || 'Kasir'}
                    </td>

                    <td className="py-2 px-2.5 text-center">
                      {tx.status === 'valid' ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-full">
                          <CheckCircle2 className="w-3 h-3" /> Sah
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-800 bg-rose-100 px-2 py-0.5 rounded-full cursor-help"
                          title={`Dibatalkan: ${tx.alasan_batal || '-'}`}
                        >
                          <Ban className="w-3 h-3" /> Batal
                        </span>
                      )}
                    </td>

                    <td className="py-2 px-2.5 text-right print:hidden">
                      <button
                        onClick={() => setSelectedTxId(tx.id)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold rounded-lg text-[11px] transition-colors cursor-pointer"
                        title="Cetak Kwitansi Resmi"
                      >
                        <Printer className="w-3.5 h-3.5" />
                        Kwitansi
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

                {/* Rekap per Pos Pembayaran (Muncul Saat Cetak) */}
        {!isLoading && (
          <div className="hidden print:block">
                      {/* Info laporan */}
            <div className="text-xs mb-3 space-y-0.5">
              
              <div className="flex">
                <span className="w-20 font-bold">Jenis</span>
                <span>: Iuran Wajib Siswa</span>
              </div>
              <div className="flex">
                <span className="w-20 font-bold">Tanggal</span>
                <span>: {formatTanggalPanjang(startDate)} s/d {formatTanggalPanjang(endDate)}</span>
              </div>
              <div className="flex">
                <span className="w-20 font-bold">Petugas</span>
                <span>
                  : {Array.from(new Set(transaksiList.map((t) => t.createdByUser?.nama_lengkap).filter(Boolean))).join(', ') || '-'}
                </span>
              </div>
            </div>

            {/* Tabel rekap */}
            <table className="w-full text-xs border-collapse border border-slate-400">

              <tbody>
                {rekapPerPos.map((r, i) => (
                  <tr key={r.pos}>
                    <td className="border border-slate-400 py-1.5 px-2 w-10 text-center">{i + 1}</td>
                    <td className="border border-slate-400 py-1.5 px-2">{r.pos}</td>
                    <td className="border border-slate-400 py-1.5 px-2 w-40 text-right">
                      Rp {r.total.toLocaleString('id-ID')}
                    </td>
                    {i === 0 && (
                      <td
                        rowSpan={rekapPerPos.length}
                        className="border border-slate-400 px-2 w-40 text-center align-middle font-bold"
                      >
                        Rp {rekapTotalNominal.toLocaleString('id-ID')}
                      </td>
                    )}
                  </tr>
                ))}
                <tr>
                  <td
                    colSpan={3}
                    className="border border-slate-400 py-2 px-2 text-right font-bold tracking-widest text-slate-500"
                  >
                    T O T A L
                  </td>
                  <td className="border border-slate-400 py-2 px-2 text-center font-bold">
                    Rp {rekapTotalNominal.toLocaleString('id-ID')}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Lembar Tanda Tangan Laporan (Muncul Saat Cetak) */}
        <div className="signature-block print-avoid-break print:grid hidden grid-cols-2 gap-8 text-center text-xs mt-6 pt-3">
          <div>
            <p className="text-slate-500">Mengetahui,</p>
            <p className="font-semibold text-slate-800">Kepala Madrasah</p>
            <div className="h-14" />
            <p className="font-bold text-slate-900 border-t border-slate-400 inline-block px-8">
              {(profil as any)?.kepala_madrasah || '( ..................................... )'}
            </p>
            {(profil as any)?.nip_kepala_madrasah && (
              <p className="text-[10px] text-slate-600">NIP. {(profil as any).nip_kepala_madrasah}</p>
            )}
          </div>
          <div>
            <p className="text-slate-500">
              {kotaProfil ? `${kotaProfil}, ` : ''}
              {new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
            <p className="font-semibold text-slate-800">Bendahara / Petugas Kasir</p>
            <div className="h-14" />
            <p className="font-bold text-slate-900 border-t border-slate-400 inline-block px-8">
              ( ..................................... )
            </p>
          </div>
        </div>
      </div>

      {/* Kwitansi Modal */}
      <KwitansiModal
        isOpen={selectedTxId !== null}
        onClose={() => setSelectedTxId(null)}
        transaksiId={selectedTxId}
      />
    </div>
  );
}
